'use client';

import React, { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Landmark, BarChart3, Globe, ShieldCheck, Users, 
  Settings, Download, Layers, CheckCircle2, ChevronDown, Monitor
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { ShinyButton } from '@/components/ui/shiny-button'
import { DashboardPreview } from '@/components/ui/dashboard-animation'

export default function AgenciesClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: "How many client workspaces can we manage under one account?",
      a: "Our Premium plan supports up to 5 fully isolated team workspaces with custom permission structures, allowing you to easily separate client assets. Custom enterprise contracts are available for larger portfolios."
    },
    {
      q: "Can we customize client branding?",
      a: "Absolutely. You can create completely white-labeled bio pages and storefronts for clients with customizable logos, colors, background styling, and layouts."
    },
    {
      q: "Are the click analytics GDPR-compliant?",
      a: "Yes. Lenklyst is built with privacy by design. We track click statistics, geolocations (country, region, and city), device categories, and referrers using anonymized processing that complies with GDPR and CCPA guidelines."
    },
    {
      q: "Can we export campaign data for clients?",
      a: "Yes, you can export raw, detailed spreadsheet logs in CSV or JSON formats at any time. You can also hook up our webhook channels to feed click events directly into client CRMs."
    }
  ]

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white font-sans">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7 space-y-6 flex flex-col items-start text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              <Landmark className="w-3.5 h-3.5" /> For Marketing &amp; Ad Agencies
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.05]">
              Scale client campaigns.<br />
              <span className="text-gray-500">Own the branding.</span><br />
              Master analytics.
            </h1>
            <p className="text-base sm:text-lg text-white/50 font-light max-w-xl leading-relaxed">
              Consolidate client links, bio pages, storefronts, and organic referral promotions in one dashboard. Share professional, GDPR-compliant campaign reports.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Link href="/auth/sign-up" className="w-full sm:w-auto">
                <ShinyButton className="w-full justify-center py-4 px-8 text-base">
                  Launch Agency Workspace <ArrowRight className="w-4 h-4 ml-1" />
                </ShinyButton>
              </Link>
              <a href="#features" className="w-full sm:w-auto text-center py-3.5 px-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all font-semibold text-sm">
                View Agency Features
              </a>
            </div>
          </div>

          {/* Right Column: Dashboard Preview */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="w-full max-w-[460px] relative z-10">
              <DashboardPreview sectionId="team" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section id="features" className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-4 mb-16 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Agency Control Engine</span>
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              White-label performance tracking.
            </h2>
            <p className="text-sm sm:text-base text-white/40 max-w-lg mx-auto font-light">
              Collaborate on multiple clients in separate sub-environments. Provide absolute link control.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1: Team collaboration */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Collaborative Spaces</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Invite employees, account managers, or client stakeholders. Assign precise permissions (editor, billing admin, viewer) to control access loops.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Multi-member team roles', 'Audit trails & access logs', 'Secure workspace isolation'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 2: UTM Campaign Parameters */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">UTM Campaign Parameters</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Track marketing channel performance. Automatically append UTM source, medium, and campaign variables to target links for deep attribution.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Visual UTM link builder templates', 'Automated campaign parameter tracking', 'Clear attribution reporting logs'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 3: Analytics */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Granular Geolocation Logs</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Analyze performance metrics in real-time. Identify campaign peaks, referrers, device specifications, browser categories, and country/city details.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Real-time geolocation mappings', 'GDPR privacy-first anonymization', 'Device type & browser breakdown reports'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Card 4: Webhooks & Exporting */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">API Webhooks &amp; CSV Export</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Export logs easily for custom agency presentations. Stream click events in real-time via webhooks to build dashboard extensions inside Slack or database servers.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Direct download click spreadsheet (CSV)', 'Event-based programmatic webhooks', 'Full REST API developers documentation'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 relative z-10 bg-black/40">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Agency FAQs
            </h2>
            <p className="text-xs sm:text-sm text-white/40 max-w-sm mx-auto font-light">
              Common operational questions from team administrators and marketers.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div 
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 relative space-y-8">
          <h2 className="text-4xl sm:text-6xl font-bold tracking-tighter uppercase">
            Own the metrics. Elevate client output.
          </h2>
          <p className="text-sm sm:text-base text-white/40 max-w-md mx-auto font-light leading-relaxed">
            Consolidate and monitor click data with the ultimate white-label friendly link analytics portal.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/sign-up">
              <ShinyButton className="px-10 py-4 text-sm font-bold">
                Deploy Workspace Free <ArrowRight className="w-4 h-4 ml-1" />
              </ShinyButton>
            </Link>
            <Link href="/pricing" className="text-white/60 hover:text-white border border-white/10 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white/20 transition-all">
              See Premium Tiers
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
